import { Injectable } from '@angular/core';
import { JwtTokenModel } from '../../models/jwt-token.model';
import { UserModel } from '../../models/user.model';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { catchError, finalize, firstValueFrom, map, mapTo, of, switchMap, tap } from 'rxjs';
import * as moment from 'moment';
import { Scopes } from '../../types/util';
import { SessionData } from '../../models/auth.model';

export class InvalidIntegrationSetupError extends Error { }

@Injectable({
  providedIn: 'root'
})
export class ActiveUserService {
  public sessionData?: SessionData;
  private initialisationError: Error | null = null;
  private tokenData?: JwtTokenModel;
  private initialised: boolean = false;
  private initialisedListeners: Array<(err: Error | null) => void> = [];
  private refreshTimeout: any;
  private tokenRefreshing?: Promise<boolean>;

  constructor(private http: HttpClient) { }

  public get id(): string | number | null {
    return this.sessionData?.practitioner?.id || null;
  }

  public get practiceId(): string | number | null {
    return this.claims ? this.claims['pid'] : null;
  }

  public get user(): UserModel | null {
    return this.sessionData?.practitioner || null;
  }

  public get token(): string | null {
    return this.tokenData?.token || null;
  }

  public get namespace(): string | null {
    return this.sessionData?.namespace || null;
  }

  public get scopes(): Scopes[] {
    return (this.sessionData?.scope.split(' ') || []) as Scopes[];
  }

  public get claims(): { [key: string]: string | number } | null {
    return this.tokenData?.claims || null;
  }

  public async loadActiveUser(force: boolean = false): Promise<UserModel | null> {
    if (this.initialised && !force) {
      return this.user;
    }
    const params = new URLSearchParams(window.location.href.split('?')[1]);
    await this.refreshSession(params.get('token'))
      .then(() => {
        this.initialised = true;
        this.initialisedListeners.forEach(cb => cb(null));
      })
      .catch(err => {
        this.initialised = false;
        this.initialisedListeners.forEach(cb => cb(err));
      });
    return this.user;
  }

  public async isAuthed(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.onInitialised(() => {
        return resolve(!!this.user);
      });
    });
  }

  public setToken(token: string) {
    this.tokenData = this.parseToken(token);
    this.queueRefresh();
  }

  public onInitialised(cb: (error: Error | null) => void) {
    if (this.initialised || this.initialisationError) {
      cb(this.initialisationError);
      return;
    }
    this.initialisedListeners.push(cb);
  }

  public logout() {
    this.clearSession();
    window.close();
  }

  public async refreshSession(token: string | null = null): Promise<boolean> {
    if (this.tokenRefreshing) return this.tokenRefreshing;
    this.tokenRefreshing = firstValueFrom(
      this.http.post<{ token: string }>(`${environment.api}/auth/token`, {
        token: token ? token : this.token
      }).pipe(
        tap(res => {
          this.setToken(res.token as string);
        }),
        switchMap(() => this.http.get<SessionData>(`${environment.api}/auth/session`)),
        tap(res => {
          this.sessionData = res;
        }),
        finalize(() => {
          this.tokenRefreshing = undefined;
        }),
        catchError(err => {
          console.error(err);
          this.logout();
          return of(false);
        }),
        map(() => true)
      )
    );
    return this.tokenRefreshing;
  }

  private clearSession() {
    this.sessionData = undefined;
    this.tokenData = undefined;
  }

  private parseToken(token: string): JwtTokenModel {
    return {
      token,
      claims: JSON.parse(atob(token.split('.')[1])) as { [key: string]: string | number }
    };
  }

  private queueRefresh() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
    if (this.claims) {
      const timeout = Math.abs(
        moment.unix(this.claims['exp'] as number)
          .subtract(3, 'minutes')
          .diff(moment(), 'milliseconds')
      );
      this.refreshTimeout = setTimeout(() => {
        this.refreshSession();
      }, timeout);
    }
  }

}

export function ActiveUserServiceFactory(provider: ActiveUserService) {
  return () => provider.loadActiveUser();
}
